import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatKgShort } from '../../utils/productionKg';
import { apiWeight, sanitizeWeight } from '../../utils/spoolLabel';

// Big-spool labels already printed for an item, shown in the spool weighing window. The worker who
// printed a label can correct its weight: the item's whole pieces are counted again and the label
// is printed again on the line's printer. A spool already rewound can no longer be changed.

interface PrintedSpool {
  id: number;
  label_code: string | null;
  shift: 'zmiana_1' | 'zmiana_2';
  weight_kg: number;
  printed_at: string | null;
  rewound: boolean;
  user: { id: number; name: string } | null;
}

interface Props {
  itemId: number;
  /** A spool is never lighter than the item's weight (column "Waga"); 0 = no minimum. */
  minWeightKg: number;
  /** The line the user is signed in on — its printer prints the corrected label. */
  lineId: number | null;
  /** Called after a correction, so the production table shows the new pieces. */
  onChanged: () => void;
}

const formatPrintedAt = (iso: string | null): string => {
  if (!iso) return '';
  const date = new Date(iso);
  return date.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const PrintedSpoolsList: React.FC<Props> = ({ itemId, minWeightKg, lineId, onChanged }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [spools, setSpools] = useState<PrintedSpool[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [weight, setWeight] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/production-items/${itemId}/spools`);
      setSpools(res.data.data ?? []);
    } catch {
      // the list is a helper — the window works without it
    }
  }, [itemId]);

  useEffect(() => {
    load();
  }, [load]);

  if (spools.length === 0) return null;

  const startEdit = (spool: PrintedSpool) => {
    setEditingId(spool.id);
    setWeight(String(spool.weight_kg).replace('.', ','));
    setNotice(null);
  };

  const save = async (spool: PrintedSpool) => {
    const parsed = parseFloat(apiWeight(weight));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setNotice({ ok: false, text: t('spoolLabel.invalidWeight') });
      return;
    }
    // Compared in grams: no floating-point drift. The backend checks the same.
    if (minWeightKg > 0 && Math.round(parsed * 1000) < Math.round(minWeightKg * 1000)) {
      setNotice({ ok: false, text: t('spoolLabel.weightBelowItem', { min: formatKgShort(minWeightKg) }) });
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch(`/production-spools/${spool.id}`, { weight_kg: apiWeight(weight), production_line_id: lineId });
      const print = res.data.print;
      setNotice(print?.printed
        ? { ok: true, text: t('spoolLabel.correctedPrinted', { printer: print.printer ?? '' }) }
        : { ok: false, text: t('spoolLabel.correctedNotPrinted', { message: print?.message ?? '' }) });
      setEditingId(null);
      await load();
      onChanged();
    } catch (error: any) {
      const data = error?.response?.data;
      setNotice({ ok: false, text: data?.errors?.weight_kg?.[0] ?? data?.message ?? t('myProduction.unknownError') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 8 }}>{t('spoolLabel.printedSpools')}</div>
      {notice && (
        <div style={{
          fontSize: 13, borderRadius: 6, padding: '8px 10px', marginBottom: 8,
          background: notice.ok ? '#d1fae5' : '#fee2e2', color: notice.ok ? '#065f46' : '#991b1b',
        }}>
          {notice.text}
        </div>
      )}
      {spools.map(spool => {
        const own = !!user && spool.user?.id === user.id;
        const editing = editingId === spool.id;
        return (
          <div key={spool.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 0', borderTop: '1px solid #f1f5f9', fontSize: 13 }}>
            <div style={{ flex: '1 1 160px', minWidth: 0 }}>
              <strong>{spool.label_code ?? `#${spool.id}`}</strong>
              <span style={{ color: '#64748b' }}>
                {' · '}{spool.shift === 'zmiana_1' ? t('table.z1') : t('table.z2')}
                {' · '}{spool.user?.name ?? '—'}
                {spool.printed_at ? ` · ${formatPrintedAt(spool.printed_at)}` : ''}
              </span>
            </div>
            {editing ? (
              <>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={weight}
                  autoFocus
                  onChange={e => {
                    const next = sanitizeWeight(e.target.value);
                    if (next !== null) setWeight(next);
                  }}
                  style={{ width: 90, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 }}
                />
                <span>kg</span>
                <button
                  onClick={() => save(spool)}
                  disabled={saving}
                  style={{ padding: '6px 12px', background: '#667eea', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
                >
                  {saving ? t('spoolLabel.sending') : t('common.save')}
                </button>
                <button
                  onClick={() => setEditingId(null)}
                  disabled={saving}
                  style={{ padding: '6px 12px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer' }}
                >
                  {t('common.cancel')}
                </button>
              </>
            ) : (
              <>
                <strong style={{ fontSize: 14 }}>{formatKgShort(spool.weight_kg)} kg</strong>
                {spool.rewound ? (
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>{t('spoolLabel.rewound')}</span>
                ) : own ? (
                  <button
                    onClick={() => startEdit(spool)}
                    style={{ padding: '6px 12px', background: '#fff', color: '#667eea', border: '1px solid #667eea', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}
                  >
                    ✎ {t('spoolLabel.correctWeight')}
                  </button>
                ) : null}
              </>
            )}
          </div>
        );
      })}
      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>{t('spoolLabel.onlyOwnHint')}</div>
    </div>
  );
};

export default PrintedSpoolsList;
